import { useMicVAD } from "@ricky0123/vad-react";
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import AudioContextService from '@/shared/lib/audio-context/audioContext';
import { useVadStore } from '@/shared/store/vad';
import {
  UseVadCoreProps,
  UseVadCoreReturn,
} from '../types/type';


// 순수한 VAD 관리 훅
export const useVadCore = ({
  config = {},
  callbacks
}: UseVadCoreProps = {}): UseVadCoreReturn => {
  const { vadSettings = {
    negativeSpeechThreshold: 0.35,
    positiveSpeechThreshold: 0.5,
    preSpeechPadFrames: 30,
    redemptionFrames: 24
  } } = config;

  // VAD Store 상태 사용
  const { vadStatus, setVadStatus } = useVadStore();

  const audioContextRef = useRef<AudioContextService | null>(null);
  const [pitch, setPitch] = useState<number>(0);
  const pitchIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // callbacks의 안정적인 참조 유지
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // 안정적인 callback 함수들 생성
  const onSpeechStartCallback = useCallback(() => {
    console.log('🎤 VAD: 음성 감지 시작');
    callbacksRef.current?.onSpeechStart?.();
  }, []);

  const onSpeechEndCallback = useCallback((audio: Float32Array) => {
    console.log('🎤 VAD: 음성 감지 종료');
    setVadStatus('paused');
    callbacksRef.current?.onSpeechEnd?.(audio);
  }, [setVadStatus]);

  const onVADMisfireCallback = useCallback(() => {
    console.log('🎤 VAD: 잘못된 감지');
    callbacksRef.current?.onVADMisfire?.();
  }, []);

  // VAD 설정 객체 메모이제이션
  const vadConfig = useMemo(() => ({
    model: "v5" as const,
    baseAssetPath: "/vad/",
    onnxWASMBasePath: "/vad/",
    positiveSpeechThreshold: vadSettings.positiveSpeechThreshold,
    negativeSpeechThreshold: vadSettings.negativeSpeechThreshold,
    preSpeechPadFrames: vadSettings.preSpeechPadFrames,
    redemptionFrames: vadSettings.redemptionFrames,
    startOnLoad: false,
    onSpeechStart: onSpeechStartCallback,
    onSpeechEnd: onSpeechEndCallback,
    onVADMisfire: onVADMisfireCallback,
  }), [
    vadSettings.positiveSpeechThreshold,
    vadSettings.negativeSpeechThreshold,
    vadSettings.preSpeechPadFrames,
    vadSettings.redemptionFrames,
    onSpeechStartCallback,
    onSpeechEndCallback,
    onVADMisfireCallback
  ]);

  // useMicVAD 훅 사용
  const vad = useMicVAD(vadConfig);


  // VAD 시작
  const start = useCallback(async () => {
    if (vad) {
      vad.start();
    }
  }, [vad]);

  // VAD 중단
  const pause = useCallback(() => {
    if (vad) {
      vad.pause();
    }
  }, [vad]);

  // Pitch 모니터링 시작
  const startPitchMonitoring = useCallback(() => {
    if (pitchIntervalRef.current) {
      clearInterval(pitchIntervalRef.current);
    }

    pitchIntervalRef.current = setInterval(() => {
      if (audioContextRef.current) {
        const currentPitch = audioContextRef.current.getPitch();
        setPitch(currentPitch);
      }
    }, 33); // 33ms마다 업데이트
  }, []);

  // Pitch 모니터링 중단
  const stopPitchMonitoring = useCallback(() => {
    if (pitchIntervalRef.current) {
      clearInterval(pitchIntervalRef.current);
      pitchIntervalRef.current = null;
    }
    setPitch(0);
  }, []);

  // AudioContext 초기화
  useEffect(() => {
    const initAudioContext = async () => {
      if (!audioContextRef.current) {
        try {
          audioContextRef.current = new AudioContextService();
          await audioContextRef.current.init();
          isInitializedRef.current = true;
        } catch (error) {
          console.error('🎤 AudioContext 초기화 실패:', error);
        }
      }
    };

    initAudioContext();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.destroy();
        audioContextRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopPitchMonitoring();
    };
  }, [stopPitchMonitoring]);

  return {
    // VAD 인스턴스
    vad,
    audioContext: audioContextRef.current,

    // 상태 (store에서 가져옴)
    isListening: vadStatus === 'listening',
    vadStatus,
    pitch,
    isInitialized: isInitializedRef.current,

    // 메서드
    start,
    pause,
    startPitchMonitoring,
    stopPitchMonitoring,
  };
};